import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { brl, fmtData, fmtMes, mesAtual, mesAnterior, proximoMes } from '../lib/format';
import { Despesa, CategoriaDespesa, Imovel, Recebimento } from '../types';
import { Modal } from '../components/Modal';

const categorias: { v: CategoriaDespesa; l: string }[] = [
  { v: 'iptu', l: 'IPTU' },
  { v: 'condominio', l: 'Condomínio' },
  { v: 'manutencao', l: 'Manutenção' },
  { v: 'agua', l: 'Água' },
  { v: 'luz', l: 'Luz' },
  { v: 'gas', l: 'Gás' },
  { v: 'seguro', l: 'Seguro' },
  { v: 'imposto', l: 'Imposto' },
  { v: 'outro', l: 'Outro' }
];

type Aba = 'recebimentos' | 'despesas';

export function Lancamentos() {
  const [aba, setAba] = useState<Aba>('recebimentos');
  const [mes, setMes] = useState(mesAtual());
  const [imovelFilter, setImovelFilter] = useState('');
  const [imoveis, setImoveis] = useState<Imovel[]>([]);
  const [recebimentos, setRecebimentos] = useState<Recebimento[]>([]);
  const [despesas, setDespesas] = useState<Despesa[]>([]);
  const [recOpen, setRecOpen] = useState(false);
  const [recEdit, setRecEdit] = useState<Recebimento | undefined>();
  const [despOpen, setDespOpen] = useState(false);
  const [despEdit, setDespEdit] = useState<Despesa | undefined>();

  const carregar = async () => {
    const q = new URLSearchParams({ mes });
    if (imovelFilter) q.set('imovel_id', imovelFilter);
    const [r, d] = await Promise.all([
      api.get<Recebimento[]>(`/recebimentos?${q}`),
      api.get<Despesa[]>(`/despesas?${q}`)
    ]);
    setRecebimentos(r);
    setDespesas(d);
  };

  useEffect(() => { api.get<Imovel[]>('/imoveis').then(setImoveis); }, []);
  useEffect(() => { carregar(); }, [mes, imovelFilter]);

  const removerRec = async (r: Recebimento) => {
    if (!confirm(`Remover recebimento de ${r.imovel_apelido}?`)) return;
    await api.del(`/recebimentos/${r.id}`);
    carregar();
  };

  const removerDesp = async (d: Despesa) => {
    if (!confirm(`Remover despesa "${d.descricao}"?`)) return;
    await api.del(`/despesas/${d.id}`);
    carregar();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold hidden sm:block">Lançamentos</h1>
        <div className="flex items-center gap-2">
          <button className="btn-ghost px-2" onClick={() => setMes(mesAnterior(mes))}>‹</button>
          <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="input w-40" />
          <button className="btn-ghost px-2" onClick={() => setMes(proximoMes(mes))}>›</button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <Tab label={`Recebimentos (${recebimentos.length})`} ativo={aba === 'recebimentos'} onClick={() => setAba('recebimentos')} />
        <Tab label={`Despesas (${despesas.length})`} ativo={aba === 'despesas'} onClick={() => setAba('despesas')} />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select className="input w-auto" value={imovelFilter} onChange={(e) => setImovelFilter(e.target.value)}>
          <option value="">Todos os imóveis</option>
          {imoveis.map((im) => <option key={im.id} value={im.id}>{im.apelido}</option>)}
        </select>
        <div className="flex-1" />
        {aba === 'recebimentos' ? (
          <button className="btn-primary" onClick={() => { setRecEdit(undefined); setRecOpen(true); }}>+ Recebimento</button>
        ) : (
          <button className="btn-primary" onClick={() => { setDespEdit(undefined); setDespOpen(true); }}>+ Despesa</button>
        )}
      </div>

      {aba === 'recebimentos' ? (
        <div className="card">
          {recebimentos.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">Nenhum recebimento.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recebimentos.map((r) => (
                <li key={r.id} className="p-3 sm:p-4 flex justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.imovel_apelido}</div>
                    <div className="text-xs text-slate-500">
                      {fmtMes(r.mes_referencia)} · {r.imovel_dono}
                      {r.data_pagamento && ` · pago ${fmtData(r.data_pagamento)}`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold">
                      {brl(r.valor_recebido ?? r.valor_previsto)}
                      {r.valor_recebido != null
                        ? <span className="badge-green ml-2">pago</span>
                        : <span className="badge-yellow ml-2">pendente</span>}
                    </div>
                    <div className="flex gap-2 justify-end mt-1">
                      <button className="text-xs text-brand-600 hover:underline" onClick={() => { setRecEdit(r); setRecOpen(true); }}>editar</button>
                      <button className="text-xs text-red-600 hover:underline" onClick={() => removerRec(r)}>remover</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="card">
          {despesas.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">Nenhuma despesa.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {despesas.map((d) => (
                <li key={d.id} className="p-3 sm:p-4 flex justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{d.descricao}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {fmtMes(d.mes_referencia)} · {d.imovel_apelido ?? 'Geral'} · {categorias.find((c) => c.v === d.categoria)?.l}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={d.pago ? 'text-slate-700 font-semibold' : 'text-red-700 font-semibold'}>
                      {brl(d.valor)}
                      <span className={d.pago ? 'badge-green ml-2' : 'badge-yellow ml-2'}>{d.pago ? 'pago' : 'pendente'}</span>
                    </div>
                    <div className="flex gap-2 justify-end mt-1">
                      <button className="text-xs text-brand-600 hover:underline" onClick={() => { setDespEdit(d); setDespOpen(true); }}>editar</button>
                      <button className="text-xs text-red-600 hover:underline" onClick={() => removerDesp(d)}>remover</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <RecebimentoForm
        open={recOpen}
        onClose={() => setRecOpen(false)}
        onSaved={carregar}
        imoveis={imoveis}
        recebimento={recOpen ? recEdit : undefined}
        mesPadrao={mes}
      />
      <DespesaForm
        open={despOpen}
        onClose={() => setDespOpen(false)}
        onSaved={carregar}
        imoveis={imoveis}
        despesa={despOpen ? despEdit : undefined}
        mesPadrao={mes}
      />
    </div>
  );
}

function Tab({ label, ativo, onClick }: { label: string; ativo: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 ${
        ativo ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-900'
      }`}
    >
      {label}
    </button>
  );
}

interface RecProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  imoveis: Imovel[];
  recebimento?: Recebimento;
  mesPadrao: string;
}

function RecebimentoForm({ open, onClose, onSaved, imoveis, recebimento, mesPadrao }: RecProps) {
  const [form, setForm] = useState({
    imovel_id: '', mes_referencia: mesPadrao,
    valor_previsto: 0, valor_recebido: '' as string | number,
    data_pagamento: '', observacao: ''
  });

  useEffect(() => {
    if (recebimento) {
      setForm({
        imovel_id: recebimento.imovel_id,
        mes_referencia: recebimento.mes_referencia,
        valor_previsto: recebimento.valor_previsto,
        valor_recebido: recebimento.valor_recebido ?? '',
        data_pagamento: recebimento.data_pagamento ?? '',
        observacao: recebimento.observacao ?? ''
      });
    } else {
      const im = imoveis.find((i) => i.ativo);
      setForm({
        imovel_id: im?.id ?? '',
        mes_referencia: mesPadrao,
        valor_previsto: im?.valor_aluguel ?? 0,
        valor_recebido: '',
        data_pagamento: '',
        observacao: ''
      });
    }
  }, [recebimento, open, mesPadrao]);

  // ao trocar imóvel, sugerir valor padrão
  useEffect(() => {
    if (!recebimento && form.imovel_id) {
      const im = imoveis.find((i) => i.id === form.imovel_id);
      if (im) setForm((f) => ({ ...f, valor_previsto: im.valor_aluguel }));
    }
  }, [form.imovel_id]);

  const submit = async () => {
    const payload = {
      ...form,
      valor_recebido: form.valor_recebido === '' ? null : Number(form.valor_recebido),
      data_pagamento: form.data_pagamento || null,
      observacao: form.observacao || null
    };
    if (recebimento) await api.put(`/recebimentos/${recebimento.id}`, payload);
    else await api.post('/recebimentos', payload);
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={recebimento ? 'Editar recebimento' : 'Novo recebimento'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={submit} disabled={!form.imovel_id || !form.valor_previsto}>Salvar</button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label">Imóvel</label>
          <select className="input" value={form.imovel_id} onChange={(e) => setForm({ ...form, imovel_id: e.target.value })} disabled={!!recebimento}>
            <option value="">—</option>
            {imoveis.map((im) => <option key={im.id} value={im.id}>{im.apelido}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Mês</label>
            <input type="month" className="input" value={form.mes_referencia} onChange={(e) => setForm({ ...form, mes_referencia: e.target.value })} disabled={!!recebimento} />
          </div>
          <div>
            <label className="label">Valor previsto</label>
            <input type="number" step="0.01" className="input" value={form.valor_previsto} onChange={(e) => setForm({ ...form, valor_previsto: Number(e.target.value) })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Valor recebido</label>
            <input type="number" step="0.01" className="input" value={form.valor_recebido} onChange={(e) => setForm({ ...form, valor_recebido: e.target.value })} placeholder="vazio = pendente" />
          </div>
          <div>
            <label className="label">Data do pagamento</label>
            <input type="date" className="input" value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">Observação</label>
          <textarea className="input" rows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}

interface DespProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  imoveis: Imovel[];
  despesa?: Despesa;
  mesPadrao: string;
}

function DespesaForm({ open, onClose, onSaved, imoveis, despesa, mesPadrao }: DespProps) {
  const [form, setForm] = useState({
    imovel_id: '' as string | null,
    categoria: 'manutencao' as CategoriaDespesa,
    descricao: '', mes_referencia: mesPadrao, valor: 0,
    data_pagamento: '', pago: 0, observacao: ''
  });

  useEffect(() => {
    if (despesa) {
      setForm({
        imovel_id: despesa.imovel_id,
        categoria: despesa.categoria,
        descricao: despesa.descricao,
        mes_referencia: despesa.mes_referencia,
        valor: despesa.valor,
        data_pagamento: despesa.data_pagamento ?? '',
        pago: despesa.pago,
        observacao: despesa.observacao ?? ''
      });
    } else {
      setForm({
        imovel_id: '',
        categoria: 'manutencao',
        descricao: '', mes_referencia: mesPadrao, valor: 0,
        data_pagamento: '', pago: 0, observacao: ''
      });
    }
  }, [despesa, open, mesPadrao]);

  const submit = async () => {
    const payload = {
      ...form,
      imovel_id: form.imovel_id || null,
      data_pagamento: form.data_pagamento || null,
      observacao: form.observacao || null
    };
    if (despesa) await api.put(`/despesas/${despesa.id}`, payload);
    else await api.post('/despesas', payload);
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={despesa ? 'Editar despesa' : 'Nova despesa'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={submit} disabled={!form.descricao || !form.valor}>Salvar</button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label">Descrição</label>
          <input className="input" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="ex: IPTU 2026, Reparo encanamento" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Categoria</label>
            <select className="input" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value as CategoriaDespesa })}>
              {categorias.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Imóvel</label>
            <select className="input" value={form.imovel_id ?? ''} onChange={(e) => setForm({ ...form, imovel_id: e.target.value })}>
              <option value="">Geral</option>
              {imoveis.map((im) => <option key={im.id} value={im.id}>{im.apelido}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Mês</label>
            <input type="month" className="input" value={form.mes_referencia} onChange={(e) => setForm({ ...form, mes_referencia: e.target.value })} />
          </div>
          <div>
            <label className="label">Valor</label>
            <input type="number" step="0.01" className="input" value={form.valor} onChange={(e) => setForm({ ...form, valor: Number(e.target.value) })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Data do pagamento</label>
            <input type="date" className="input" value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} />
          </div>
          <label className="flex items-end gap-2 text-sm pb-2">
            <input type="checkbox" checked={!!form.pago} onChange={(e) => setForm({ ...form, pago: e.target.checked ? 1 : 0 })} />
            Despesa paga
          </label>
        </div>
        <div>
          <label className="label">Observação</label>
          <textarea className="input" rows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}
